import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { punchApi, usersApi, type PunchListResponse, type PunchAssigneeInput } from '@/lib/apiClient';
import { Button } from '@/components';
import { chunk, bulkOutcome, type BulkOutcome } from './punchModel';

type Panel = 'reassign' | 'due' | 'pass' | null;

/**
 * The bulk bar (#518 phase 2, design "Bulk bar"): shown above the table while
 * items are selected. One change for all of them — reassign, set or clear the
 * due date, pass the ball — or their sub sheets. The server applies each item
 * through the single-item rules and says which it skipped and why.
 */
export default function PunchBulkBar({ projectId, data, ids, onDone, onSheets, onApplied, onClear, onOutcome }: {
  projectId: number;
  data: PunchListResponse;
  ids: number[];
  /** Something changed: refresh the list. */
  onDone: () => void;
  onSheets: () => void;
  /**
   * A change finished, with what each item did. The table unticks the ones
   * that changed from its selection as it is NOW — so ticks made while this
   * ran aren't lost (review).
   */
  onApplied: (results: { itemId: number; ok: boolean }[]) => void;
  onClear: () => void;
  /**
   * What the change did. Shown by the table, not here: when everything
   * changes the selection empties and this bar goes away, and the result
   * must still be seen.
   */
  onOutcome: (o: BulkOutcome | null) => void;
}) {
  const [panel, setPanel] = useState<Panel>(null);
  const [busy, setBusy] = useState(false);
  const [assignee, setAssignee] = useState('ew_crew');
  const [due, setDue] = useState('');
  const subsQ = useQuery({ queryKey: ['punch', projectId, 'subs'], queryFn: () => punchApi.subs(projectId), staleTime: 60_000 });
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: () => usersApi.me(), staleTime: 5 * 60 * 1000 });
  const ao = !!data.list?.requiresAoApproval;
  const labelOf = (id: number) => data.items.find((i) => i.id === id)?.label ?? `#${id}`;
  const n = ids.length;

  const run = async (body: Omit<Parameters<typeof punchApi.bulk>[1], 'itemIds'>, what: string) => {
    setBusy(true);
    onOutcome(null);
    // In requests the API takes: "select all shown" can pick more than one
    // request's worth (review of this change).
    const results: { itemId: number; ok: boolean; error?: string }[] = [];
    let error: string | null = null;
    for (const part of chunk(ids)) {
      try {
        const r = await punchApi.bulk(projectId, { itemIds: part, ...body });
        results.push(...r.results);
        // The server stopped this request on an error (answered, not failed):
        // the later requests mustn't go — a stopped change stays stopped
        // (Codex on #577).
        if (r.stopped) {
          error = 'Stopped: the change hit an error partway. The items still ticked weren’t changed, or aren’t confirmed — check them in the list and try again.';
          break;
        }
      } catch (e) {
        // What changed before this is kept and shown. This request may or
        // may not have gone through (its reply is what failed), and the rest
        // weren't tried — so all of those stay ticked, and the refreshed list
        // shows where each stands.
        error = `Stopped: ${(e as Error)?.message ?? 'that didn’t work'}. The items still ticked may not have changed — check them in the list and try again.`;
        break;
      }
    }
    const o = bulkOutcome(results, what, labelOf, error);
    onOutcome(o);
    setPanel(null);
    onApplied(results);
    setBusy(false);
    onDone();
  };

  const toAssignee = (v: string): PunchAssigneeInput | null =>
    v === 'none' ? null : v === 'ew_crew' ? { type: 'ew_crew' } : { type: 'sub', name: v.slice(4) };

  return (
    <div className="pl-bulk" role="region" aria-label="Change the selected items">
      <div className="pl-bulk-bar">
        <strong>{n} selected</strong>
        <button type="button" className="pl-bulk-btn" aria-pressed={panel === 'reassign'} disabled={busy}
          onClick={() => setPanel(panel === 'reassign' ? null : 'reassign')}>Reassign…</button>
        <button type="button" className="pl-bulk-btn" aria-pressed={panel === 'due'} disabled={busy}
          onClick={() => setPanel(panel === 'due' ? null : 'due')}>Change Due Date…</button>
        <button type="button" className="pl-bulk-btn" aria-pressed={panel === 'pass'} disabled={busy}
          onClick={() => setPanel(panel === 'pass' ? null : 'pass')}>Pass To…</button>
        <button type="button" className="pl-bulk-btn" disabled={busy} onClick={onSheets}>Sheets for These</button>
        <span className="pl-spacer" />
        <button type="button" className="pl-bulk-btn" disabled={busy} onClick={() => { onOutcome(null); onClear(); }}>Clear</button>
      </div>

      {panel === 'reassign' && (
        <div className="pl-bulk-panel pl-row">
          <label className="pl-field" style={{ minWidth: 260 }}><span>Assign {n} item{n === 1 ? '' : 's'} to</span>
            <select value={assignee} onChange={(e) => setAssignee(e.target.value)}>
              <option value="none">Unassigned (New items only)</option>
              <option value="ew_crew">{data.ewCrewLabel}</option>
              {(subsQ.data?.subs ?? []).map((s) => <option key={s.name} value={`sub:${s.name}`}>{s.name}</option>)}
            </select>
          </label>
          <Button variant="primary" size="sm" isLoading={busy} onClick={() => run({ op: 'reassign', assignee: toAssignee(assignee) }, 'Reassigned')}>
            Reassign
          </Button>
        </div>
      )}
      {panel === 'due' && (
        <div className="pl-bulk-panel pl-row">
          <label className="pl-field"><span>New due date</span>
            <input type="date" value={due} onChange={(e) => setDue(e.target.value)} />
          </label>
          <Button variant="primary" size="sm" isLoading={busy} disabled={!due} onClick={() => run({ op: 'due', dueDate: due }, 'Due date set')}>
            Set Due Date
          </Button>
          <Button variant="secondary" size="sm" disabled={busy} onClick={() => run({ op: 'due', dueDate: null }, 'Due date cleared')}>
            Clear Due Date
          </Button>
        </div>
      )}
      {panel === 'pass' && (
        <div className="pl-bulk-panel pl-row">
          <span className="pl-muted">Pass the ball on {n} item{n === 1 ? '' : 's'} to</span>
          <Button variant="secondary" size="sm" disabled={busy} onClick={() => run({ op: 'pass_to', bicTo: 'ew' }, 'Ball passed to E&W')}>E&amp;W</Button>
          {me && (
            <Button variant="secondary" size="sm" disabled={busy} onClick={() => run({ op: 'pass_to', bicTo: 'ew', bicUserId: me.id }, 'Ball passed to you')}>Me</Button>
          )}
          <Button variant="secondary" size="sm" disabled={busy} onClick={() => run({ op: 'pass_to', bicTo: 'assignee' }, 'Ball passed to the assignee')}>
            Their assignee
          </Button>
          {ao && (
            <Button variant="secondary" size="sm" disabled={busy} onClick={() => run({ op: 'pass_to', bicTo: 'ao' }, 'Ball passed to the architect/owner')}>
              Architect/Owner
            </Button>
          )}
        </div>
      )}

    </div>
  );
}

/** What the last bulk change did: under the bar, and still there once the bar has gone. */
export function BulkOutcomeNote({ outcome, onDismiss }: { outcome: BulkOutcome; onDismiss: () => void }) {
  return (
    <div className="pl-bulk" role="status">
      {outcome.ok && (
        <div className="pl-note pl-row" style={{ justifyContent: 'space-between' }}>
          <span>{outcome.ok}</span>
          <button type="button" className="pl-link" onClick={onDismiss}>Dismiss</button>
        </div>
      )}
      {outcome.skipped.length > 0 && (
        <div className="pl-warn">
          Skipped {outcome.skipped.length}:
          <ul className="pl-bulk-skipped">{outcome.skipped.map((s) => <li key={s}>{s}</li>)}</ul>
          They’re still ticked.
        </div>
      )}
      {outcome.error && <div className="pl-error" role="alert">{outcome.error}</div>}
    </div>
  );
}
